/* ============================================================================
   KOTT Lab — Hero — production module
   ----------------------------------------------------------------------------
   • A full-bleed WebGL "silk" brand gradient fills the hero background. It
     ignites (fades up) from the intro loader (`kott:loader:done`, with
     fallbacks) and drifts gently over time, reacting softly to the pointer.
   • The headline + lede + chips sit on top. The chips are links that smooth-
     scroll the visitor to the matching home section.
   • No pin, no device, no screen swaps. Pauses rAF when off-screen. Falls back
     to a CSS gradient without WebGL; under reduced-motion the loop never starts
     (CSS shows a static gradient hero) — but the chip jumps still work (instant).
   ============================================================================ */
(function(){
  const root=document.querySelector('.hero-liquid');
  if(!root) return;
  const reduced=window.kottReduceMotion?window.kottReduceMotion():matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---- service chips → smooth-scroll to the matching section (always wired,
  // even under reduced motion; instant jump when motion is reduced) ----
  function jump(e){
    const el=e.currentTarget;
    const sel=el.getAttribute('data-hero-jump');
    let target=sel?document.querySelector(sel):null;
    if(!target){const h=el.getAttribute('href');if(h&&h.charAt(0)==='#')target=document.querySelector(h);}
    if(!target) return; // nothing to scroll to → let the native anchor handle it
    e.preventDefault();
    const soft=window.kottReduceMotion?!window.kottReduceMotion():!matchMedia('(prefers-reduced-motion: reduce)').matches;
    const y=target.getBoundingClientRect().top+window.scrollY-20;
    window.scrollTo({top:Math.max(0,y),behavior:soft?'smooth':'auto'});
  }
  root.querySelectorAll('[data-hero-jump]').forEach(el=>el.addEventListener('click',jump));

  const cv=root.querySelector('.liquid');
  const wrap=root.querySelector('.wrap');
  const rs=root.style;
  if(!cv||!wrap) return;

  // ---- WebGL silk (domain-warped brand gradient) ----
  let gl=null; try{ gl=cv.getContext('webgl')||cv.getContext('experimental-webgl'); }catch(e){}
  let glOK=!!gl,prog,uT,uR,uM;
  const FS=`precision highp float;
    uniform float uTime;uniform vec2 uRes;uniform vec2 uMouse;
    float hash(vec2 p){p=fract(p*vec2(123.34,456.21));p+=dot(p,p+45.32);return fract(p.x*p.y);}
    float vnoise(vec2 p){vec2 i=floor(p),f=fract(p);vec2 u=f*f*(3.0-2.0*f);
      return mix(mix(hash(i),hash(i+vec2(1,0)),u.x),mix(hash(i+vec2(0,1)),hash(i+vec2(1,1)),u.x),u.y);}
    float fbm(vec2 p){float v=0.0,a=0.5;for(int i=0;i<5;i++){v+=a*vnoise(p);p*=2.03;a*=0.5;}return v;}
    void main(){
      vec2 uv=gl_FragCoord.xy/uRes.xy;
      float asp=uRes.x/uRes.y;
      vec2 p=vec2(uv.x*asp,uv.y);
      float t=uTime*0.045;
      vec2 mo=(uMouse-0.5)*0.18;

      vec2 q=vec2(fbm(p*1.3+t+mo), fbm(p*1.3-t+5.2));
      vec2 r=vec2(fbm(p*1.5+q*1.7+t*1.2), fbm(p*1.5+q*1.7-t));
      float f=fbm(p*1.2+r*1.9+mo);
      float band=sin((p.x*0.7+p.y*0.9)*3.0+r.x*3.2+t*2.2)*0.5+0.5;

      vec3 cream=vec3(0.957,0.964,0.984);
      vec3 indigo=vec3(0.341,0.435,0.965);
      vec3 green=vec3(0.0,0.643,0.486);
      vec3 peach=vec3(1.0,0.451,0.255);

      vec3 col=cream;
      col=mix(col,indigo,smoothstep(0.28,0.92,f)*0.40*band);
      col=mix(col,peach, smoothstep(0.32,0.93,r.x)*0.34*(1.0-band));
      col=mix(col,green, smoothstep(0.40,0.95,q.y)*0.22);
      col*=mix(0.978,1.0,smoothstep(0.0,1.0,uv.y));

      // very faint grain
      col+=(hash(uv*uRes.xy+t)-0.5)*0.010;
      gl_FragColor=vec4(col,1.0);}`;
  const VS=`attribute vec2 a;void main(){gl_Position=vec4(a,0.,1.);}`;
  function sh(ty,src){const s=gl.createShader(ty);gl.shaderSource(s,src);gl.compileShader(s);if(!gl.getShaderParameter(s,gl.COMPILE_STATUS)){console.warn('[hero-liquid]',gl.getShaderInfoLog(s));glOK=false;}return s;}
  if(glOK){try{
    prog=gl.createProgram();gl.attachShader(prog,sh(gl.VERTEX_SHADER,VS));gl.attachShader(prog,sh(gl.FRAGMENT_SHADER,FS));gl.linkProgram(prog);gl.useProgram(prog);
    const b=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,b);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,3,-1,-1,3]),gl.STATIC_DRAW);
    const loc=gl.getAttribLocation(prog,'a');gl.enableVertexAttribArray(loc);gl.vertexAttribPointer(loc,2,gl.FLOAT,false,0,0);
    uT=gl.getUniformLocation(prog,'uTime');uR=gl.getUniformLocation(prog,'uRes');uM=gl.getUniformLocation(prog,'uMouse');
  }catch(e){glOK=false;}}
  if(!glOK){root.classList.add('is-nogl');cv.style.background='radial-gradient(120% 120% at 30% 30%,#dfe4fb,#f4f6fb 55%,#fbeee8)';}

  function size(){const d=Math.min(2,devicePixelRatio||1);cv.width=Math.round(innerWidth*d);cv.height=Math.round(innerHeight*d);if(glOK)gl.viewport(0,0,cv.width,cv.height);}
  size();addEventListener('resize',size,{passive:true});
  const mouse={x:.5,y:.5,tx:.5,ty:.5};
  addEventListener('pointermove',e=>{mouse.tx=e.clientX/innerWidth;mouse.ty=1-e.clientY/innerHeight;},{passive:true});

  // ---- ignition from loader (continuous, with fallbacks) ----
  let ignite=0,igniting=false,ignited=false,igStart=0;
  const IG_MS=reduced?420:980;
  function beginIgnite(){if(igniting||ignited)return;igniting=true;igStart=performance.now();}
  if(window.__kottLoaderDone){beginIgnite();}
  else{
    document.addEventListener('kott:loader:done',beginIgnite,{once:true});
    if(window.Shopify&&window.Shopify.designMode) setTimeout(beginIgnite,300);
    setTimeout(beginIgnite,6200);
  }

  let raf=0,tStart=performance.now();
  function frame(now){
    raf=requestAnimationFrame(frame);
    try{
    mouse.x+=(mouse.tx-mouse.x)*0.05;mouse.y+=(mouse.ty-mouse.y)*0.05;
    const tt=(now-tStart)/1000;

    if(igniting&&!ignited){const e=Math.min(1,(now-igStart)/IG_MS);ignite=1-Math.pow(1-e,3);if(e>=1){ignited=true;igniting=false;ignite=1;}}
    cv.style.opacity=ignite.toFixed(3);
    // headline + chips fade up with the background
    rs.setProperty('--hl-introA',ignite.toFixed(3));
    rs.setProperty('--hl-introY',((1-ignite)*-18).toFixed(1)+'px');

    if(glOK){gl.uniform1f(uT,tt);gl.uniform2f(uR,cv.width,cv.height);gl.uniform2f(uM,mouse.x,mouse.y);
      gl.drawArrays(gl.TRIANGLES,0,3);}
    }catch(e){ if(!window.__hlErr){window.__hlErr=1;console.error('[hero-liquid]',e);} }
  }

  let io=null;
  function teardown(){
    if(raf){cancelAnimationFrame(raf);raf=0;}
    if(io){io.disconnect();io=null;}
  }
  function startLoop(){
    if(!raf)raf=requestAnimationFrame(frame);
    if(!io && 'IntersectionObserver' in window){
      io=new IntersectionObserver(ents=>{ents.forEach(en=>{
        if(en.isIntersecting && !raf){raf=requestAnimationFrame(frame);}
        else if(!en.isIntersecting && raf){cancelAnimationFrame(raf);raf=0;}
      });},{rootMargin:'120px'});
      io.observe(wrap);
    }
  }

  // Single active reduce-motion handler (survives theme-editor section reloads).
  function onReduceChange(e){
    if(e.detail && e.detail.reduced) teardown();   // live stop → static CSS hero
    else location.reload();                         // re-enable → clean re-init
  }
  if(window.__hlReduceHandler) window.removeEventListener('kott:reduce-motion-change', window.__hlReduceHandler);
  window.__hlReduceHandler=onReduceChange;
  window.addEventListener('kott:reduce-motion-change', onReduceChange);

  if(!reduced) startLoop();   // when reduced, CSS shows the static hero; no loop
})();
